import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ButtonBase, Menu, MenuItem, Tooltip } from "@mui/material";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import logo from "@/assets/eCarehealth-White-logo.svg";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import {
  NAV_BAR_COLORS,
  NAV_BAR_LABELS,
  NAV_BAR_LAYOUT,
  NAV_BAR_MENU_PAPER_SX,
  NAV_BAR_POPUP_IDS,
  NAV_BAR_TYPOGRAPHY,
} from "../nav-bar-constant";

export interface NavBarBrandProps {
  /** Groups the user may work in; the switcher lists these. */
  providerGroups: string[];
  activeProviderGroup?: string;
  /** Omitted while switching is display-only — the list still renders. */
  onSelectProviderGroup?: (providerGroup: string) => void;
}

const expandIcon = (isOpen: boolean) => {
  const Icon = isOpen ? ExpandLessIcon : ExpandMoreIcon;
  return (
    <Icon
      sx={{
        fontSize: NAV_BAR_LAYOUT.TAB_ICON_SIZE,
        color: NAV_BAR_COLORS.ON_BAR_MUTED,
      }}
    />
  );
};

const NavBarBrand = ({
  providerGroups,
  activeProviderGroup,
  onSelectProviderGroup,
}: NavBarBrandProps) => {
  const hasGroupList = providerGroups.length > 0;

  const groupLabel = (
    <Text
      fontSize={NAV_BAR_TYPOGRAPHY.OVERLINE_FONT_SIZE}
      c={NAV_BAR_COLORS.ON_BAR_MUTED}
      noWrap
    >
      {activeProviderGroup}
    </Text>
  );

  return (
    <Flex flexDirection="column" gap={NAV_BAR_LAYOUT.BRAND_GAP} flexShrink={0}>
      <img
        src={logo}
        alt={NAV_BAR_LABELS.LOGO_ALT}
        width={NAV_BAR_LAYOUT.LOGO_WIDTH}
        height={NAV_BAR_LAYOUT.LOGO_HEIGHT}
      />

      {!activeProviderGroup ? null : !hasGroupList ? (
        groupLabel
      ) : (
        <PopupState
          variant="popover"
          popupId={NAV_BAR_POPUP_IDS.PROVIDER_GROUP}
        >
          {(popupState) => (
            <>
              <Tooltip title={NAV_BAR_LABELS.PROVIDER_GROUP_TOOLTIP}>
                <ButtonBase
                  disableRipple
                  sx={{ gap: 0.25, p: 0 }}
                  {...bindTrigger(popupState)}
                >
                  {groupLabel}
                  {expandIcon(popupState.isOpen)}
                </ButtonBase>
              </Tooltip>

              <Menu
                {...bindMenu(popupState)}
                slotProps={{
                  paper: { sx: NAV_BAR_MENU_PAPER_SX },
                }}
              >
                {providerGroups.map((group) => (
                  <MenuItem
                    key={group}
                    selected={group === activeProviderGroup}
                    onClick={() => {
                      onSelectProviderGroup?.(group);
                      popupState.close();
                    }}
                  >
                    <Flex alignItems="center" gap={1} width="100%">
                      <CheckOutlinedIcon
                        sx={{
                          fontSize: NAV_BAR_LAYOUT.TAB_ICON_SIZE,
                          visibility:
                            group === activeProviderGroup
                              ? "visible"
                              : "hidden",
                        }}
                      />
                      <Text
                        fontSize={NAV_BAR_TYPOGRAPHY.LABEL_FONT_SIZE}
                        c={NAV_BAR_COLORS.MENU_ITEM_TEXT}
                      >
                        {group}
                      </Text>
                    </Flex>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </PopupState>
      )}
    </Flex>
  );
};

export default NavBarBrand;
